<template>
  <span>
    <v-alert
      v-if="crawlRequestIdToCompare && changesDetected && schedulerStillExists"
      border="left"
      colored-border
      type="info"
      color="primary"
      elevation="2"
      class="mt-6"
    >
      This result is expected? Compare future checks against this result.
      <v-btn @click="setNewComparisionBaseMethod()" color="primary" text> Update <Caption></Caption>omparison base</v-btn>
    </v-alert>
    <v-alert border="left" dense colored-border type="error" elevation="2" v-if="baseUpdatedError">
      There was an error updating the comparison base: {{ baseUpdatedError }}
    </v-alert>
    <v-alert dense text type="success" v-if="baseUpdated">
      Updated the base. From now on all future crawls will be compared with this result.
    </v-alert>
    <v-alert v-if="bannerNotFoundOnPages > 0 && isWPPage" class="mt-6" color="grey darken-1" dark border="left" prominent>
      <v-row align="center">
        <v-col class="shrink">
          <v-img
            lazy-src="/images/beautiful-cookie-banner-logo.png"
            max-height="50"
            max-width="50"
            style="border-radius: 100%"
            src="/images/beautiful-cookie-banner-logo.png"
          ></v-img>
        </v-col>
        <v-col class="grow">
          <div style="font-weight: 600">Need a Cookie Banner for your WordPress site?</div>
          Try the free and easy-to-use 👉
          <a style="color: #ffffff" target="_blank" href="https://beautiful-cookie-banner.com/">Beautiful Cookie Banner</a> - it's
          fast, supports consent modes, and works with many popular privacy laws.
        </v-col>
      </v-row>
    </v-alert>
    <ResultEvaluation v-if="!thisIsAComparison" v-bind:result="result" class="mt-6" @message="messageReceiver" />

    <v-card class="px-4 py-8 mt-6" :tile="false">
      <v-btn
        v-if="showDepth === 'pages' && !thisIsAComparison"
        small
        @click="setSummary()"
        color="primary"
        class="float-md-right"
        elevation="0"
      >
        {{ buttonTextGoToSummary }}</v-btn
      >
      <v-btn
        v-if="showDepth === 'summary' && !thisIsAComparison"
        small
        @click="setPages()"
        color="blue-grey"
        class="float-md-right white--text"
        elevation="0"
      >
        {{ buttonTextGoToPages }}</v-btn
      >
      <v-radio-group v-model="showDepth" row v-if="!thisIsAComparison">
        <v-radio label="Summary" value="summary" @click="setSummary()"></v-radio>
        <v-radio label="Pages" value="pages" @click="setPages()"></v-radio>
      </v-radio-group>
      <ReportingIndexSummary v-bind:result="result" v-if="showDepth === 'summary' && !thisIsAComparison" />
      <ReportingIndexPagesOverview
        v-bind:crawlRequestId="crawlRequestId"
        v-bind:thisIsAComparison="thisIsAComparison"
        v-bind:countDiffTotal="countDiffTotal || 0"
        v-if="showDepth === 'pages' || thisIsAComparison"
        :key="thisIsAComparison + countDiffTotal + crawlRequestId"
      />
    </v-card>
  </span>
</template>

<script>
import ResultEvaluation from './ResultEvaluation.vue';
import ReportingIndexSummary from './ReportingIndexSummary.vue';
import ReportingIndexPagesOverview from './ReportingIndexPagesOverview.vue';
import { setNewComparisionBase } from '../../../utils/apiData';

export default {
  name: 'ReportingCookieScanner',
  props: {
    result: Object,
  },
  components: {
    ResultEvaluation,
    ReportingIndexSummary,
    ReportingIndexPagesOverview,
  },
  data() {
    return {
      baseUpdated: false,
      baseUpdatedError: false,
      showDepth: 'summary',
      buttonTextGoToSummary: 'go to summary',
      buttonTextGoToPages: 'go to page details',
      tab: null,
      tableConfigCookies: {
        sortBy: ['thirdParty', 'percentage', 'domain', 'name'],
        sortDesc: [true, true, false, true],
        loadingText: 'A good page crawl needs a couple of minutes. Please be patient.',
        noDataText: 'No Cookies found..',
        headers: [
          {
            text: 'Name',
            value: 'name',
          },
          { text: this.$t('tableHeadFoundOnPagesPercent'), value: 'percentage' },
          { text: 'Domain', value: 'domain' },
          { text: 'Third-party', value: 'thirdParty' },
          {
            text: this.$t('tableHeadPagesWithEvent'),
            value: 'examplePagesFound',
          },
          {
            text: this.$t('tableHeadPagesWithMissingEvent'),
            value: 'examplePagesNotFound',
          },
          { text: this.$t('Set By'), value: 'source.type' },
          { text: this.$t('Initiator'), value: 'source.source', name: 'source' },
        ],
      },
      tableConfigLocalStorage: {
        sortBy: ['name'],
        sortDesc: [false],
        loadingText: 'A good page crawl needs a couple of minutes. Please be patient.',
        noDataText: 'No localStorage Entries found.',
        headers: [
          {
            text: 'Name',
            value: 'name',
          },
          { text: this.$t('tableHeadFoundOnPagesPercent'), value: 'percentage' },
          { text: this.$t('tableHeadFoundOnPages'), value: 'absolute' },
          {
            text: this.$t('tableHeadPagesWithEvent'),
            value: 'examplePagesFound',
          },
          {
            text: this.$t('tableHeadPagesWithMissingEvent'),
            value: 'examplePagesNotFound',
          },
          { text: this.$t('Initiator'), value: 'source.source', name: 'source' },
        ],
      },
    };
  },
  async created() {
    if (new URLSearchParams(window.location.search).get('pages') || new URLSearchParams(window.location.search).get('details')) {
      this.showDepth = 'pages';
      return;
    }
    this.showDepth = 'summary';
  },
  computed: {
    isWPPage() {
      return this.result.cms === 'wordpress';
    },
    bannerNotFoundOnPages() {
      if (!this.result || typeof this.result.bannerNotFoundOnPages === 'undefined') {
        return -2;
      }
      if (this.result.bannerNotFoundOnPages === -1) {
        return ' - ';
      }
      return this.result.bannerNotFoundOnPages;
    },
    crawlRequestIdToCompare() {
      if (!this.result || !this.result.crawlRequestIdToCompare) {
        return false;
      }
      return this.result.crawlRequestIdToCompare;
    },
    schedulerStillExists() {
      return this.result.schedulerStillExists || false;
    },
    changesDetected() {
      if (!this.result || typeof this.result.countDiffTotal !== 'number') {
        return false;
      }
      return this.result.countDiffTotal;
    },
    thisIsAComparison() {
      if (!this.result || !this.result.crawlRequestIdToCompare) {
        return false;
      }
      return true;
    },
    countDiffTotal() {
      if (!this.result || typeof this.result.countDiffTotal !== 'number') {
        return false;
      }
      return this.result.countDiffTotal;
    },
    crawlRequestId() {
      if (!this.result || !this.result.crawlRequestId) {
        return '';
      }
      return this.result.crawlRequestId;
    },
    localStorageTableData() {
      if (!this.result || !this.result.table || !this.result.table.localStorage.rowData) {
        return [];
      }

      return this.result.table.localStorage.rowData;
    },
    cookieTableData() {
      if (!this.result || !this.result.table || !this.result.table.cookies.rowData) {
        return [];
      }

      return this.result.table.cookies.rowData;
    },
    numberOfCustomJSEval() {
      if (
        !this.result ||
        !this.result.table ||
        !this.result.table.customScriptResult ||
        !this.result.table.customScriptResult.rowData
      ) {
        return 0;
      }
      return this.result.table.customScriptResult.rowData.length;
    },
  },
  methods: {
    async setNewComparisionBaseMethod() {
      const result = await setNewComparisionBase(this.result.schedulerId, this.result.crawlRequestId);
      if (result.success) {
        this.baseUpdated = true;
        return true;
      }

      this.baseUpdatedError = result.data && result.data.message ? result.data.message : '';
      return false;
    },
    setSummary() {
      this.showDepth = 'summary';
      this.buttonText = this.buttonTextGoToPages;
      this.$router.push({
        name: 'SummaryReporting',
        params: { hostnameToCrawl: this.result.startHostname },
        query: { ucrid: this.crawlRequestId, summary: true },
      });
    },
    messageReceiver(message) {
      if (message.type && message.type === 'setSummary') {
        this.setSummary();
      }

      if (message.type && message.type === 'navDrawer') {
        this.drawer = message.value;
      }
    },
    setPages() {
      this.showDepth = 'pages';
      this.buttonText = this.buttonTextGoToSummary;
      this.$router.push({
        name: 'SummaryReporting',
        params: { hostnameToCrawl: this.result.startHostname },
        query: { ucrid: this.crawlRequestId, pages: true },
      });
    },
  },
};
</script>
